import React, { useContext } from 'react'
import { css } from 'emotion'
import { Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import { NavLink } from 'react-router-dom'
import { CloseIcon } from 'assets/icons'
import uiConstants from 'config/constants/ui'
import ScrollView from 'src/components/utils/ScrollView'
import { useMe, useMyNotificationCount } from 'src/graphql/hooks'
import ProtoThemeContext from 'src/libraries/contexts/ProtoTheme'
import NavVertical from '~/components/MainMenu/NavVertical'

const DrawerMenu = ({
   horizontalPlacement,
   onCloseButtonClick,
   onOverlayClick,
   visible
}) => {
   const { protoTheme: theme } = useContext(ProtoThemeContext)
   const { me, loading: meLoading, isLoggedIn } = useMe()
   const { notificationCount } = useMyNotificationCount()

   const panelProps = {
      [horizontalPlacement]: '0px'
   }

   const openAnimationPosition = horizontalPlacement === 'left'
      ? 'translate3d(-100%, 0, 0)'
      : 'translate3d(100%, 0, 0)'

   // When DrawerMenu is visible, Panel becomes a ScrollView component
   // We only want this to happen when it is visible as it will disabled scrolling
   // for the rest of the contents underneath
   const DrawerMenuPanel = visible ? ScrollView : View

   const buttonProps = {
      size: '3',
      color: theme.tmpMainMenuButtonTextColor,
      lineHeight: '180%'
   }

   return (
      <View
         className={css`pointer-events: ${visible ? 'auto' : 'none'};`}
         position="fixed"
         top="0px"
         left="0px"
         right="0px"
         bottom="0px"
         textAlign="left"
         zIndex={uiConstants.zIndexes.drawerMenu + 9999}>
         <View
            id="globalNavDrawerControlOverlay"
            onClick={onOverlayClick}
            onKeyPress={onOverlayClick}
            role="button"
            tabIndex={0}
            position="absolute"
            top="0px"
            left="0px"
            right="0px"
            bottom="0px"
            backgroundColor="rgba(0,0,0,0.5)"
            transition="opacity 300ms"
            transform={visible ? 'translate3d(0, 0, 0)' : 'translate3d(-100%, 0, 0)'}
            opacity={visible ? '1' : '0'}
         />
         <DrawerMenuPanel
            {...panelProps}
            position="absolute"
            top="0px"
            bottom="0px"
            width="330px[a-b] 400px[c-f]"
            backgroundColor={theme.tmpMainMenuBackgroundColor === 'transparent'
               ? '#000'
               : theme.tmpMainMenuBackgroundColor
            }
            transition="300ms"
            transform={visible ? 'translate3d(0, 0, 0)' : openAnimationPosition}
            opacity={visible ? '1' : '0'}
            scroll="on">
            <View
               float="left"
               width="100%"
               height="70px"
               display="flex"
               justifyContent="flex-end"
               alignItems="center"
               paddingRight="20px">
               <div
                  id="globalNavDrawerControlCloseButton"
                  onClick={onCloseButtonClick}
                  onKeyPress={onCloseButtonClick}
                  role="button"
                  tabIndex={0}
                  style={{ outline: 'none' }}>
                  <CloseIcon
                     height="24px"
                     width="24px"
                     color={theme.tmpMainMenuTextColor}
                  />
               </div>
            </View>
            <View
               onClick={onCloseButtonClick}
               float="left"
               width="100%"
               paddingHorizontal={theme.tmpSpacing}
               paddingVertical="10px">
               {isLoggedIn && (
                  <>
                     <NavLink to="/profile">
                        <Text
                           {...buttonProps}
                           color={theme.tmpMainMenuButtonActiveTextColor}
                           weight="medium">
                           {me.fullName}
                        </Text>
                     </NavLink>
                     <NavLink to="/messages">
                        <View display="flex" alignItems="center" position="relative">
                           <Text {...buttonProps}>
                              Messages
                           </Text>
                           {notificationCount && (
                              <View
                                 display="flex"
                                 justifyContent="center"
                                 alignItems="center"
                                 width="18px"
                                 height="18px"
                                 borderRadius="3px"
                                 marginLeft="10px"
                                 backgroundColor={theme.tmpNotificationBadgeColor}>
                                 <Text size="0.85" color={theme.tmpNotificationBadgeTextColor}>
                                    {notificationCount}
                                 </Text>
                              </View>
                           )}
                        </View>
                     </NavLink>
                     <NavLink to="/logout">
                        <Text {...buttonProps}>
                           Logout
                        </Text>
                     </NavLink>
                  </>
               )}
               <View>
                  {!meLoading && !isLoggedIn && (
                     <NavLink to="/login">
                        <Text {...buttonProps}>
                           Login
                        </Text>
                     </NavLink>
                  )}
               </View>
            </View>
            <View
               onClick={onCloseButtonClick}
               float="left"
               width="100%"
               paddingTop={theme.tmpSpacing}
               paddingLeft={theme.tmpSpacing}>
               <NavVertical />
            </View>
         </DrawerMenuPanel>
      </View>
   )
}

DrawerMenu.propTypes = {
   horizontalPlacement: PropTypes.oneOf(['left', 'right']),
   onCloseButtonClick: PropTypes.func.isRequired,
   onOverlayClick: PropTypes.func.isRequired,
   visible: PropTypes.bool
}

DrawerMenu.defaultProps = {
   horizontalPlacement: 'right',
   visible: false
}

export default DrawerMenu
